import twttr from 'twitter-text'

const NAME_MAX_LENGTH = 50
// Letters/marks plus the few separators real names use, and the zero-width joiners that Persian and
// Indic spellings depend on. A leading apostrophe is allowed for names like "'t Hooft", but a letter
// still has to follow it. No digits, slashes, colons, @ or markup.
// eslint-disable-next-line no-misleading-character-class -- the joiners are deliberate, not a mis-split emoji
const NAME_ALLOWED_REGEX = /^['’]?\p{L}[\p{L}\p{M}\u200C\u200D .,'’·-]*$/u
// `\p{C}` minus the zero-width joiners, which are orthographically meaningful rather than control
// characters: stripping them silently misspells names such as "Ali<ZWNJ>Reza".
const NAME_CONTROL_REGEX = /[^\P{C}\u200C\u200D]+/gu
// The separators the allowlist permits can be wedged against a dot to slip a domain past the URL
// extractor ("evil'.com", "evil-.com"), so the URL check also runs on a copy with them collapsed.
const DOT_SEPARATORS_REGEX = /['’-]*\.['’-]*/gu

/** Normalizes spacing and drops control characters (incl. CR/LF used for header injection). */
function sanitizeName(name: string) {
  return name
    // NFC first so the length limit does not depend on how the name was typed: "José" is 4 code
    // points composed and 5 decomposed.
    .normalize('NFC')
    // Whitespace before control characters: tabs and newlines are control characters, and deleting
    // them outright would merge "John\tSmith" into a single word.
    .replace(/\s+/gu, ' ')
    .replace(NAME_CONTROL_REGEX, '')
    // A backtick in a name is a mistyped apostrophe, never markup worth keeping.
    .replace(/`/gu, '’')
    .trim()
}

/**
 * True when the value still reads as a domain or link once name punctuation is discounted.
 * twitter-text validates the TLD, so "St.John" and "A.J." are names while "x.com" and "t.co" are not.
 */
function looksLikeUrl(value: string) {
  return twttr.extractUrls(value).length > 0
    || twttr.extractUrls(value.replace(DOT_SEPARATORS_REGEX, '.')).length > 0
}

/** Returns the sanitized name, or null when it is empty, too long or looks like a URL/injection payload. */
export function validateName(name: string) {
  const value = sanitizeName(name)
  if (!value || Array.from(value).length > NAME_MAX_LENGTH) {
    return null
  }
  if (!NAME_ALLOWED_REGEX.test(value) || looksLikeUrl(value)) {
    return null
  }
  return value
}
